// Billing Provider Interface
// Defines common contract for all payment providers (Paddle, Stripe, etc.)

export interface PriceInfo {
  productId: string;
  amount: number;
  currency: string;
  currencySymbol: string;
  tax: number;
  total: number;
  formatted: {
    amount: string;
    tax: string;
    total: string;
  };
}

export interface CheckoutOptions {
  priceId: string;  // Main recurring price ID (always required)
  oneTimePriceId?: string;  // Optional one-time price for initial trial payment
  email?: string;
  country?: string;
  postalCode?: string;
  discountCode?: string;
  customData?: Record<string, any>;
  successUrl?: string;
  cancelUrl?: string;
  // Display information for the checkout popup
  displayInfo?: {
    amount: string;
    plan: string;
  };
}

export interface CheckoutResult {
  status: 'success' | 'cancelled' | 'error';
  transactionId?: string;
  error?: string;
}

export interface BillingProviderConfig {
  environment: 'sandbox' | 'production';
  vendorId?: string; // Legacy Paddle vendor ID (deprecated)
  clientToken?: string; // Client token for authentication (Paddle, etc.)
  publicKey?: string; // Public key for providers like Stripe
}

export type CheckoutMode = 'popup' | 'inline' | 'redirect';

// Events that billing providers can emit
export interface BillingEvents {
  'checkout:opened': void;
  'checkout:closed': CheckoutResult;
  'checkout:success': { transactionId: string };
  'checkout:cancelled': void;
  'checkout:error': { error: string };
  'prices:updated': PriceInfo[];
}

// Main interface that all billing providers must implement
export interface BillingProvider {
  // Provider identification
  readonly name: 'paddle' | 'stripe' | 'other';

  // Initialization
  initialize(config: BillingProviderConfig): Promise<void>;
  isInitialized(): boolean;

  // Price management
  fetchPrices(priceIds: string[]): Promise<PriceInfo[]>;
  formatPrice(amount: number, currency: string): string;

  // Checkout management
  openCheckout(options: CheckoutOptions, mode: CheckoutMode): Promise<void>;
  closeCheckout(): void;

  // Payment methods
  isApplePayAvailable(): Promise<boolean>;
  isGooglePayAvailable(): Promise<boolean>;

  // Event handling
  on<K extends keyof BillingEvents>(event: K, handler: (data: BillingEvents[K]) => void): void;
  off<K extends keyof BillingEvents>(event: K, handler: (data: BillingEvents[K]) => void): void;

  // Cleanup
  destroy(): void;
}

// Base implementation with common functionality
export abstract class BaseBillingProvider implements BillingProvider {
  abstract readonly name: 'paddle' | 'stripe' | 'other';
  protected initialized = false;
  protected eventHandlers: Map<keyof BillingEvents, Set<Function>> = new Map();

  abstract initialize(config: BillingProviderConfig): Promise<void>;

  isInitialized(): boolean {
    return this.initialized;
  }

  abstract fetchPrices(priceIds: string[]): Promise<PriceInfo[]>;
  abstract openCheckout(options: CheckoutOptions, mode: CheckoutMode): Promise<void>;
  abstract closeCheckout(): void;
  abstract isApplePayAvailable(): Promise<boolean>;
  abstract isGooglePayAvailable(): Promise<boolean>;

  formatPrice(amount: number, currency: string): string {
    // Default implementation - can be overridden
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    });
    return formatter.format(amount);
  }

  on<K extends keyof BillingEvents>(event: K, handler: (data: BillingEvents[K]) => void): void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set());
    }
    this.eventHandlers.get(event)?.add(handler);
  }

  off<K extends keyof BillingEvents>(event: K, handler: (data: BillingEvents[K]) => void): void {
    this.eventHandlers.get(event)?.delete(handler);
  }

  protected emit<K extends keyof BillingEvents>(event: K, data: BillingEvents[K]): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.forEach(handler => handler(data));
    }
  }

  destroy(): void {
    this.eventHandlers.clear();
    this.initialized = false;
  }
}